import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useCostLineItems,
  useCostSummary,
  useProjectsList,
  type CostKind,
  type CostLineItem,
  type CostLineItemsOrder,
  type CostLineItemsSort,
  type CostLineItemsStatus,
  type CostSummary,
  type Project,
} from "../api";
import { SpendBars } from "../components/SpendBars";
import type { SpendStack } from "../components/SpendChartEcharts";
import { PageHeader } from "../components/PageHeader";
import { Tooltip } from "../components/Tooltip";
import { ViewAiTraceButton } from "../components/ViewAiTraceButton";
import { type AgentConfig, useAgentConfigs } from "../api/hooks/useAgentConfigs";
import {
  planLegText,
  platformIndex,
  platformLabel,
  splitPlatform,
} from "../utils/platforms";
import { slotKeyName } from "../utils/agentSlotText";
import { formatUsd } from "../utils/estimateRunCost";
import { runLinkPath } from "./learningAgentPaths";
import { DEFAULT_SLOT } from "../api/agentSlot";
import { formatDayLong } from "../utils/formatRelative";
import { projectDisplayName } from "../utils/projectName";
import { unpricedMessage } from "../utils/unpricedMessage";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import styles from "./CostsRoute.module.css";

// "YYYY-MM-DD" for a Date in the operator's local calendar (the value
// shape <input type="date"> expects + the BE's day key).
const toIsoDay = (d: Date): string => {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const todayIso = (): string => toIsoDay(new Date());

// Only `default`: no configuration to tell apart, so the page reads as before configs.
const hasNonDefaultConfig = (keys: string[] | undefined): boolean =>
  !!keys?.some((key) => key !== DEFAULT_SLOT);
const daysAgoIso = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toIsoDay(d);
};

// The cost sources the BE prices, with operator-facing labels. Keep in sync
// with the BE's source values (cost_context) + the CostKind union.
const ALL_KINDS: CostKind[] = [
  "agent_run",
  "procurement",
  "extraction",
  "global_data",
  "clause_lists",
  "conversation",
  "stateful_agent_run",
  "agent_daily_sweep",
  "gmail_sync",
  "mailbox_sweep",
  "mailbox_confidentiality",
  "slack_sync",
  "admin_health",
  "operational",
  "unattributed",
];
const KIND_LABEL: Record<CostKind, string> = {
  agent_run: "Agent runs",
  procurement: "Procurement",
  extraction: "Extraction",
  global_data: "Global data",
  clause_lists: "Clause list comments",
  conversation: "Conversations",
  stateful_agent_run: "Learning agent runs",
  agent_daily_sweep: "Daily portfolio sweeps",
  gmail_sync: "Gmail sync",
  mailbox_sweep: "Mailbox sweep",
  mailbox_confidentiality: "Confidentiality guard",
  slack_sync: "Slack voice notes",
  admin_health: "Admin health",
  operational: "Operational",
  unattributed: "Unattributed",
};

// Scope sentinels that are NOT project ids. Both were rendering through the
// deleted-project fallback, so a portfolio-wide learning run and a per-mailbox
// sync both reported as spend on a deleted project - which is not merely ugly,
// it invites somebody to go looking for a project that was never there.
const PORTFOLIO_SCOPE = "__portfolio__";
const MAILBOX_SCOPE_PREFIX = "mailbox:";
// A conversation's temporary extraction (backend ``temporary_extraction.SCOPE_PREFIX``).
const CONVERSATION_SCOPE_PREFIX = "conversation:";

const scopeLabel = (projectId: string): string | null => {
  if (projectId === PORTFOLIO_SCOPE) return "Portfolio-wide";
  if (projectId.startsWith(MAILBOX_SCOPE_PREFIX)) return "All projects on this inbox";
  if (projectId.startsWith(CONVERSATION_SCOPE_PREFIX)) return "Temporary extraction in a conversation";
  return null;
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];
const DEFAULT_PAGE_SIZE = 100;

// "14 May 2026, 09:32" from an ISO timestamp. Line items are individual
// calls, so unlike the per-day chart we show the time too.
const formatTimestamp = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Compact a ledger model id for display, naming any platform but AWS:
// "eu.anthropic.claude-opus-4-8-v1" -> "opus-4-8",
// "anthropic-api/claude-sonnet-5-5" -> "sonnet-5-5 (Anthropic API)".
const shortModel = (m: string | null): string => {
  const { platform, model } = splitPlatform(m ?? "unknown");
  const short = model
    .replace(/^eu\.anthropic\./, "")
    .replace(/^anthropic\./, "")
    .replace(/^claude-/, "")
    .replace(/-v\d+$/, "");
  return platform === "aws-bedrock" ? short : `${short} (${platformLabel(platform)})`;
};

// "47s" / "3m 20s of audio transcribed"; "" when none.
const formatAudio = (seconds: number): string => {
  if (seconds <= 0) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m > 0 ? `${m}m ${s}s` : `${s}s`} of audio transcribed`;
};

// Model column text: a partial breakdown of what makes up the row's cost.
// The model share - "75% opus-4-8, 25% haiku-4-5" for a run that spanned
// multiple models (model_usage is pre-sorted DESC by token share on the BE),
// or the sole model for a single-model run - plus, when the run issued web
// searches (billed per query with no tokens, so absent from model_usage), a
// "· N searches" suffix: e.g. "opus-4-8 · 2 searches", and likewise the audio a
// run transcribed (Slack voice notes). "-" when none of these.
const formatModelUsage = (item: CostLineItem): string => {
  const mu = item.model_usage;
  let models: string;
  if (!mu || mu.length === 0)
    models = item.model_id ? shortModel(item.model_id) : "";
  else if (mu.length === 1) models = shortModel(mu[0].model_id);
  else models = mu.map((m) => `${m.pct}% ${shortModel(m.model_id)}`).join(", ");

  const n = item.web_search_count ?? 0;
  const searches = n > 0 ? `${n} ${n === 1 ? "search" : "searches"}` : "";
  const audio = formatAudio(item.transcription_seconds ?? 0);

  const base = [models, searches, audio].filter(Boolean).join(" · ") || "-";

  // A consolidated row (e.g. the "Operational" wrong-site-sweep row) carries a
  // free-text note pointing at the change that explains its spend. Append it
  // after the model so the Model cell reads "sonnet-4-6, See PR #314 ...".
  const note = item.model_note?.trim();
  if (note) return base === "-" ? note : `${base}, ${note}`;
  return base;
};

// AI Costs page: unifies cost tracking + AI-trace debugging. A single date
// range drives both the per-day spend chart and the server-side-paginated
// list of every priced AI call below, so the operator can scope a
// window and see what it cost AND which calls drove the cost in one place.
export const CostsRoute = () => {
  useDocumentTitle("AI costs");
  // The trailing 30 calendar days including today, the window the nav badge's
  // /cost/summary {days: 30} resolves to, so the two totals agree.
  const [start, setStart] = useState(() => daysAgoIso(29));
  const [end, setEnd] = useState(() => todayIso());

  const rangeValid = Boolean(start && end && start <= end);

  // Only fire the cost summary once the range is well-formed - the hook
  // short-circuits on a half-set / inverted range so we never spend a
  // 400 round-trip on it.
  const costQuery = useCostSummary(
    rangeValid ? { start, end } : { days: undefined },
  );

  const projectsQuery = useProjectsList();
  const configs = useAgentConfigs().data?.configs;
  // Only trust a missing project_id to mean "deleted" once the projects
  // list has actually loaded. While it's still in flight the list is empty,
  // so every row would otherwise be mislabelled "Deleted project".
  const projectsResolved = projectsQuery.isSuccess;

  return (
    <section>
      <PageHeader
        title="AI costs"
        description={
          <>
            Pick a date range to see per-day AI spend, on AWS Bedrock and any
            other platform, and the individual AI calls that drove it - across
            agent runs, procurement, extraction, and global data. Open any
            call&apos;s AI trace to see exactly what the model saw and reasoned.
            USD, computed by the backend; each platform&apos;s bill is the
            source of truth.
          </>
        }
      />

      <DateRange
        start={start}
        end={end}
        onStart={setStart}
        onEnd={setEnd}
        valid={rangeValid}
      />

      <SpendChartCard
        query={costQuery}
        start={start}
        end={end}
        valid={rangeValid}
        projects={projectsQuery.data ?? []}
        projectsResolved={projectsResolved}
        configs={configs}
      />

      <LineItemsList
        projects={projectsQuery.data ?? []}
        projectsResolved={projectsResolved}
        configs={configs}
        configsWithSpend={costQuery.data?.by_config?.map((c) => c.config)}
        start={start}
        end={end}
        rangeValid={rangeValid}
      />

      <EstimateCaveat />
    </section>
  );
};

// ---------------------------------------------------------------------- //
// Date range selector - drives both the chart and the line-item list.
// ---------------------------------------------------------------------- //
const DateRange = ({
  start,
  end,
  onStart,
  onEnd,
  valid,
}: {
  start: string;
  end: string;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
  valid: boolean;
}) => (
  <div className={styles.rangeCard}>
    <div className={styles.controls}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>From</span>
        <input
          type="date"
          className={styles.dateInput}
          value={start}
          max={end || todayIso()}
          onChange={(e) => onStart(e.target.value)}
        />
      </label>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>To</span>
        <input
          type="date"
          className={styles.dateInput}
          value={end}
          min={start}
          max={todayIso()}
          onChange={(e) => onEnd(e.target.value)}
        />
      </label>
    </div>
    {!valid && (
      <div className={styles.notice}>
        Pick a start date on or before the end date.
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------- //
// Per-day spend chart for the selected range.
// ---------------------------------------------------------------------- //
const SpendChartCard = ({
  query,
  start,
  end,
  valid,
  projects,
  projectsResolved,
  configs,
}: {
  query: ReturnType<typeof useCostSummary>;
  start: string;
  end: string;
  valid: boolean;
  projects: Project[];
  projectsResolved: boolean;
  configs: AgentConfig[] | undefined;
}) => {
  return (
    <div className={styles.chartCard}>
      <div className={styles.rangeHeader}>
        <span className={styles.rangeLabel}>
          {formatDayLong(start)} - {formatDayLong(end)}
        </span>
        {valid && query.data && (
          <span className={styles.rangeTotal}>
            {formatUsd(query.data.total_usd)}
          </span>
        )}
      </div>

      {!valid ? (
        <div className={styles.notice}>Pick a valid range to see spend.</div>
      ) : query.isLoading && !query.data ? (
        <div className={styles.notice}>Loading spend...</div>
      ) : query.error || !query.data ? (
        <div className={styles.error}>
          Couldn&apos;t load spend:{" "}
          {(query.error as Error)?.message ?? "no data"}
        </div>
      ) : (
        <>
          {query.data.unpriced_count > 0 && (
            <UnpricedWarning
              count={query.data.unpriced_count}
              unknownModels={query.data.unknown_models}
            />
          )}
          <PlatformSpendChart data={query.data} />
          <SpendCaps configs={configs} />
          <SpendBreakdowns
            data={query.data}
            projects={projects}
            projectsResolved={projectsResolved}
            configs={configs}
          />
        </>
      )}
    </div>
  );
};

type Grouping = "all" | "platform";

// The spend chart, either as each day's total or stacked by the platforms picked.
const PlatformSpendChart = ({ data }: { data: CostSummary }) => {
  const [grouping, setGrouping] = useState<Grouping>("all");
  const platforms = useMemo(
    () => (data.by_platform ?? []).filter((p) => p.cost_usd > 0).map((p) => p.platform),
    [data.by_platform],
  );
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const stacks = useMemo<SpendStack[] | undefined>(
    () =>
      grouping === "platform"
        ? platforms
            .filter((p) => !hidden.has(p))
            .map((p) => ({
              name: platformLabel(p),
              colourIndex: platformIndex(p),
              values: data.days.map((d) => d.by_platform?.[p] ?? 0),
            }))
        : undefined,
    [grouping, platforms, hidden, data.days],
  );
  const toggle = (platform: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  return (
    <>
      <div className={styles.groupControls}>
        <label>
          Group by{" "}
          <select
            value={grouping}
            onChange={(e) => setGrouping(e.target.value as Grouping)}
          >
            <option value="all">All spend</option>
            <option value="platform">Platform</option>
          </select>
        </label>
        {grouping === "platform" &&
          platforms.map((p) => (
            <label key={p} className={styles.groupChip}>
              <input
                type="checkbox"
                checked={!hidden.has(p)}
                onChange={() => toggle(p)}
              />{" "}
              {platformLabel(p)}
            </label>
          ))}
      </div>
      <SpendBars
        days={data.days}
        stacks={stacks}
        height={stacks ? 190 : 150}
        emptyLabel="No inference spend in this range. Pick a wider window, or check a range with priced calls."
      />
    </>
  );
};

// A spend cap on a config's plan, and how far through it this environment is.
const SpendCaps = ({ configs }: { configs: AgentConfig[] | undefined }) => {
  const capped = (configs ?? []).flatMap((c) =>
    (c.platform_plan ?? [])
      .filter((leg) => leg.cap_usd !== null)
      .map((leg) => ({ config: c.label, leg })),
  );
  if (capped.length === 0) return null;
  return (
    <ul className={styles.spendCaps}>
      {capped.map(({ config, leg }) => (
        <li key={`${config}-${leg.platform}`}>
          {config}: {planLegText(leg)}
        </li>
      ))}
    </ul>
  );
};

const UnpricedWarning = ({
  count,
  unknownModels,
}: {
  count: number;
  unknownModels: string[];
}) => (
  <div className={styles.unpriced} role="status">
    <span className={styles.unpricedIcon} aria-hidden>
      ⚠
    </span>
    {unpricedMessage(count, unknownModels)}
  </div>
);

const SpendBreakdowns = ({
  data,
  projects,
  projectsResolved,
  configs,
}: {
  data: CostSummary;
  projects: Project[];
  projectsResolved: boolean;
  configs: AgentConfig[] | undefined;
}) => {
  // Resolve project_id -> name; a row whose id isn't in the live projects
  // list belongs to a deleted project (whose spend we keep) - label it so
  // it reads as deleted rather than as a bare id. Until the projects list
  // has loaded we can't tell deleted from not-yet-resolved, so show a
  // neutral id-suffixed placeholder instead of claiming it's deleted.
  const nameById: Record<string, string> = {};
  for (const p of projects) nameById[p.project_id] = projectDisplayName(p);
  return (
    <div className={styles.breakdowns}>
      {data.web_searches && data.web_searches.count > 0 && (
        // Web search is billed per query (no tokens), so it shows as its own
        // dimension: the search COUNT (in the row name) + its isolated cost,
        // beside the token spend. Hidden when no searches ran in the window.
        <SpendBreakdown
          title="Web search"
          rows={[
            {
              name: `${data.web_searches.count} ${
                data.web_searches.count === 1 ? "search" : "searches"
              }`,
              cost_usd: data.web_searches.cost_usd,
            },
          ]}
        />
      )}
      {data.transcription && data.transcription.seconds > 0 && (
        <SpendBreakdown
          title="Voice note transcription"
          rows={[
            {
              name: formatAudio(data.transcription.seconds),
              cost_usd: data.transcription.cost_usd,
            },
          ]}
        />
      )}
      <SpendBreakdown
        title="By platform"
        rows={(data.by_platform ?? []).map((p) => ({
          name: platformLabel(p.platform),
          cost_usd: p.cost_usd,
        }))}
      />
      <SpendBreakdown
        title="By model"
        rows={data.by_model.map((m) => ({ name: m.model, cost_usd: m.cost_usd }))}
      />
      <SpendBreakdown
        title="By source"
        rows={data.by_kind.map((k) => ({
          name: KIND_LABEL[k.kind] ?? k.kind,
          cost_usd: k.cost_usd,
        }))}
      />
      <SpendBreakdown
        title="By status"
        rows={data.by_status.map((s) => ({
          name: s.status === "failed" ? "Failed" : "Succeeded",
          cost_usd: s.cost_usd,
        }))}
      />
      <SpendBreakdown
        title="By agent mode"
        rows={data.by_mode.map((m) => ({ name: m.mode, cost_usd: m.cost_usd }))}
      />
      {hasNonDefaultConfig(data.by_config?.map((c) => c.config)) && data.by_config && (
        <SpendBreakdown
          title="By agent configuration"
          rows={data.by_config.map((c) => ({
            name: slotKeyName(c.config, configs),
            cost_usd: c.cost_usd,
          }))}
        />
      )}
      <SpendBreakdown
        title="By project"
        rows={data.by_project.map((p) => ({
          // Prefer the BE-stamped "<name> (deleted)" label for a deleted project;
          // else resolve a live project's name; else fall back. (SpendBreakdown
          // keys rows by name+index, so a repeated deleted label can't collide.)
          name:
            scopeLabel(p.project_id) ??
            p.deleted_project_label ??
            nameById[p.project_id] ??
            (projectsResolved
              ? `Deleted project (${p.project_id.slice(0, 8)})`
              : `Project ${p.project_id.slice(0, 8)}`),
          cost_usd: p.cost_usd,
        }))}
      />
    </div>
  );
};

const BREAKDOWN_COLLAPSED_ROWS = 6;

const SpendBreakdown = ({
  title,
  rows,
}: {
  title: string;
  rows: { name: string; cost_usd: number }[];
}) => {
  const [expanded, setExpanded] = useState(false);
  const collapsible = rows.length > BREAKDOWN_COLLAPSED_ROWS;
  const visible =
    collapsible && !expanded ? rows.slice(0, BREAKDOWN_COLLAPSED_ROWS) : rows;
  return (
    <div>
      <h4 className={styles.breakdownTitle}>{title}</h4>
      {rows.length === 0 ? (
        <div className={styles.breakdownEmpty}>None.</div>
      ) : (
        <ul className={styles.breakdownList}>
          {visible.map((r, i) => (
            // Index-suffixed key: two deleted projects can share a label, and
            // the list is a static sorted list re-rendered wholesale, so the
            // index is stable and collision-free.
            <li key={`${r.name}-${i}`} className={styles.breakdownRow}>
              <span className={styles.breakdownName} title={r.name}>
                {r.name}
              </span>
              <span className={styles.breakdownValue}>
                {formatUsd(r.cost_usd)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {collapsible && (
        <button
          type="button"
          className={styles.breakdownToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? "Show fewer" : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------- //
// Server-side paginated + sorted list of every priced AI call in range.
// ---------------------------------------------------------------------- //
const LineItemsList = ({
  projects,
  projectsResolved,
  configs,
  configsWithSpend,
  start,
  end,
  rangeValid,
}: {
  projects: Project[];
  projectsResolved: boolean;
  configs: AgentConfig[] | undefined;
  /** The slots with agent-run spend in range: the config filter's options. */
  configsWithSpend: string[] | undefined;
  start: string;
  end: string;
  rangeValid: boolean;
}) => {
  const [kinds, setKinds] = useState<CostKind[]>([]);
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [configFilter, setConfigFilter] = useState<string>("all");
  const [status, setStatus] = useState<CostLineItemsStatus>("all");
  const [sort, setSort] = useState<CostLineItemsSort>("date");
  const [order, setOrder] = useState<CostLineItemsOrder>("desc");
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState<number>(1);

  // project_id -> display name, resolved once from the projects list so
  // each row can show a human name without an N+1 lookup per render.
  const nameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of projects) m[p.project_id] = projectDisplayName(p);
    return m;
  }, [projects]);

  // Project options sorted by display name, case-insensitive.
  const projectOptions = useMemo(
    () =>
      [...projects]
        .map((p) => ({ id: p.project_id, name: projectDisplayName(p) }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, "en-GB", { sensitivity: "base" }),
        ),
    [projects],
  );

  // Reset to page 1 whenever the range, filters, sort, order, or page size
  // change - the current page number is meaningless against a new result set.
  useEffect(() => {
    setPage(1);
  }, [start, end, kinds, projectFilter, configFilter, status, sort, order, pageSize]);

  const query = useCostLineItems({
    start: rangeValid ? start : undefined,
    end: rangeValid ? end : undefined,
    kinds: kinds.length ? kinds : undefined,
    project_id: projectFilter === "all" ? undefined : projectFilter,
    config: configFilter === "all" ? undefined : configFilter,
    status,
    sort,
    order,
    page,
    page_size: pageSize,
  });

  const data = query.data;
  const total = data?.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 1;
  const items = data?.items ?? [];

  const toggleKind = (kind: CostKind) => {
    setKinds((prev) =>
      prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind],
    );
  };

  return (
    <div className={styles.traceCard}>
      <div className={styles.traceHead}>
        <div>
          <h3 className={styles.traceTitle}>AI calls in range</h3>
          <p className={styles.traceLede}>
            Every priced AI call in the selected window, across all
            sources. Open a trace to debug what the model saw.
          </p>
        </div>
        <div className={styles.traceFilters}>
          <div className={styles.filterField}>
            <span className={styles.filterLabel}>Source</span>
            <div className={styles.kindChips}>
              {ALL_KINDS.map((kind) => {
                const active = kinds.includes(kind);
                return (
                  <button
                    key={kind}
                    type="button"
                    className={`${styles.kindChip} ${active ? styles.kindChipActive : ""}`}
                    aria-pressed={active}
                    onClick={() => toggleKind(kind)}
                  >
                    {KIND_LABEL[kind]}
                  </button>
                );
              })}
            </div>
          </div>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Project</span>
            <select
              className={styles.select}
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
            >
              <option value="all">All projects</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          {(hasNonDefaultConfig(configsWithSpend) || configFilter !== "all") && (
            <label className={styles.filterField}>
              <span className={styles.filterLabel}>Configuration</span>
              <select
                className={styles.select}
                value={configFilter}
                onChange={(e) => setConfigFilter(e.target.value)}
              >
                <option value="all">All</option>
                {[
                  ...new Set([
                    ...(configsWithSpend ?? []),
                    ...(configFilter === "all" ? [] : [configFilter]),
                  ]),
                ].map((key) => (
                  <option key={key} value={key}>
                    {slotKeyName(key, configs)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Status</span>
            <select
              className={styles.select}
              value={status}
              onChange={(e) => setStatus(e.target.value as CostLineItemsStatus)}
            >
              <option value="all">All</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Sort</span>
            <select
              className={styles.select}
              value={sort}
              onChange={(e) => setSort(e.target.value as CostLineItemsSort)}
            >
              <option value="date">Date</option>
              <option value="cost">Cost</option>
            </select>
          </label>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Order</span>
            <select
              className={styles.select}
              value={order}
              onChange={(e) => setOrder(e.target.value as CostLineItemsOrder)}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </label>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Per page</span>
            <select
              className={styles.select}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {!rangeValid ? (
        <div className={styles.note}>Pick a valid range to list AI calls.</div>
      ) : query.isLoading && !data ? (
        <div className={styles.note}>Loading AI calls...</div>
      ) : query.error ? (
        <div className={styles.error}>
          Couldn&apos;t load AI calls:{" "}
          {(query.error as Error)?.message ?? "no data"}
        </div>
      ) : total === 0 ? (
        <div className={styles.note}>
          No AI calls in this range
          {kinds.length || projectFilter !== "all" || configFilter !== "all"
            ? " for the selected filters"
            : ""}
          . Widen the range or clear the filters.
        </div>
      ) : (
        <>
          <div className={styles.traceSummary}>
            {total.toLocaleString("en-GB")} AI call{total === 1 ? "" : "s"}
            {query.isFetching ? " - updating..." : ""}
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>When</th>
                <th className={styles.th}>Source</th>
                <th className={styles.th}>Project</th>
                <th className={styles.th}>Model</th>
                <th className={`${styles.th} ${styles.thNumeric}`}>Cost</th>
                <th className={styles.th}>Trace</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <LineItemRow
                  key={item.id}
                  item={item}
                  configName={
                    item.config && item.config !== DEFAULT_SLOT
                      ? slotKeyName(item.config, configs)
                      : undefined
                  }
                  projectName={
                    item.project_id ? nameById[item.project_id] : undefined
                  }
                  projectsResolved={projectsResolved}
                />
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={query.isFetching}
          />
        </>
      )}
    </div>
  );
};

const LineItemRow = ({
  item,
  configName,
  projectName,
  projectsResolved,
}: {
  item: CostLineItem;
  /** Set for a slot other than default. */
  configName?: string;
  projectName?: string;
  projectsResolved: boolean;
}) => (
  <tr className={styles.row}>
    <td className={styles.td}>{formatTimestamp(item.occurred_at)}</td>
    <td className={styles.td}>
      {item.run_id ? (
        <Link
          className={styles.kindBadge}
          to={runLinkPath(item.run_id)}
          title="Open the agent run this spend belongs to"
        >
          {KIND_LABEL[item.kind] ?? item.kind}
        </Link>
      ) : (
        <span className={styles.kindBadge}>{KIND_LABEL[item.kind] ?? item.kind}</span>
      )}
      {configName && <span className={styles.configBadge}>{configName}</span>}
      {item.failed && (
        <Tooltip content="This AI invocation failed (errored mid-run). Its partial token spend is still counted here; open the trace to see where it failed.">
          <span className={styles.failedBadge}>⚠ Failed</span>
        </Tooltip>
      )}
    </td>
    <td className={styles.td}>
      {item.project_id
        ? (scopeLabel(item.project_id) ??
          projectName ??
          (item.deleted_project_label ? (
            // The project was deleted but its spend + trace are deliberately
            // kept, and the BE preserved the name: show "<name> (deleted)" so
            // the row stays attributable. Authoritative even before the live
            // projects list resolves, so it never flashes the "..." placeholder.
            <span className={styles.deletedProject} title={item.project_id}>
              {item.deleted_project_label}
            </span>
          ) : projectsResolved ? (
            // A run/upload whose project_id no longer resolves to a live
            // project and carries no preserved label (a legacy delete): label
            // it so it reads as deleted, not as a broken row - and the Trace
            // button beside it still works.
            <span className={styles.deletedProject} title={item.project_id}>
              Deleted project
            </span>
          ) : (
            // Projects list hasn't loaded yet, so we can't tell deleted from
            // not-yet-resolved. Show a neutral placeholder rather than wrongly
            // flashing "Deleted project" while the data is in flight.
            <span className={styles.loadingProject} title={item.project_id}>
              ...
            </span>
          )))
        : "(global)"}
    </td>
    <td
      className={`${styles.td} ${styles.tdModel}`}
      title={
        item.model_usage
          ? item.model_usage
              .map((m) => `${m.pct}% ${m.model_id ?? "unknown"}`)
              .join(", ")
          : undefined
      }
    >
      {formatModelUsage(item)}
    </td>
    <td className={`${styles.td} ${styles.tdNumeric}`}>
      {item.unpriced ? (
        <Tooltip content="This call's model isn't in the price table, so it couldn't be priced and is excluded from the total.">
          <span className={styles.unpricedTag}>⚠ unpriced</span>
        </Tooltip>
      ) : (
        formatUsd(item.cost_usd)
      )}
    </td>
    <td className={styles.td}>
      <ViewAiTraceButton
        surface={item.trace_surface}
        artefactId={item.trace_artefact_id}
        label="Trace"
        hasTrace={item.has_trace}
        modelUsage={item.model_usage}
      />
    </td>
  </tr>
);

const Pagination = ({
  page,
  totalPages,
  onPrev,
  onNext,
  disabled,
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  disabled: boolean;
}) => (
  <div className={styles.pagination}>
    <button
      type="button"
      className={styles.pageBtn}
      onClick={onPrev}
      disabled={disabled || page <= 1}
    >
      Prev
    </button>
    <span className={styles.pageLabel}>
      Page {page} of {totalPages}
    </span>
    <button
      type="button"
      className={styles.pageBtn}
      onClick={onNext}
      disabled={disabled || page >= totalPages}
    >
      Next
    </button>
  </div>
);

// ---------------------------------------------------------------------- //
// Estimate caveat - what these figures do and don't include.
// ---------------------------------------------------------------------- //
const EstimateCaveat = () => (
  <p className={styles.caveat}>
    These figures estimate token cost only - each platform&apos;s bill (AWS,
    Anthropic, Google Cloud) is the source of truth. A few low-volume ephemeral calls (Gmail query translation,
    /invoke, /stream) aren&apos;t itemised.
  </p>
);
